import LoansPage, { DEFAULT_PER_YEAR, loansQuery } from "@app/pages/loans/loans-page";
import type { loansPage_Query } from "@app/queries/loansPage_Query.graphql";
import { environment } from "@app/relay/relay-environment";
import { pageHead } from "@app/lib/page-title";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { loadQuery } from "react-relay";

type LoansSearch = { perYear?: number };

export const Route = createFileRoute("/_authed/loans")({
  // How deep the candidate list goes lives in the URL so a triage session is
  // shareable exactly as it was read. The view itself is page-owned and all-time,
  // exempt from the global date-range lens like the cash-position screen — the
  // debt curve is cumulative and a loan's repayments span years.
  validateSearch: (search: Record<string, unknown>): LoansSearch => ({
    perYear: typeof search.perYear === "number" ? search.perYear : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) =>
    loadQuery<loansPage_Query>(
      environment,
      loansQuery,
      { perYear: deps.perYear ?? DEFAULT_PER_YEAR },
      { fetchPolicy: "network-only" }
    ),
  head: () => pageHead("Loans"),
  component: LoansRoute,
});

function LoansRoute() {
  const queryRef = Route.useLoaderData();
  const { perYear } = Route.useSearch();

  return (
    <ErrorBoundary fallback={<div className="p-6 text-red-500">Error loading loans</div>}>
      <Suspense fallback={<div className="p-6 text-gray-500">Loading loans…</div>}>
        <LoansPage queryRef={queryRef} perYear={perYear ?? DEFAULT_PER_YEAR} />
      </Suspense>
    </ErrorBoundary>
  );
}
